'use client';

import { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { usePathname, useRouter, useSearchParams } from '@/lib/router';
import {
  computePeriodDateRange,
  type PeriodPreset,
  type PeriodSelection,
} from '@/lib/period-presets';
import {
  resolvePeriodSelectionParams,
  writeStoredPeriodSelection,
} from '@/lib/period-selection-storage';

/**
 * Global period selection — single source of truth for the application's
 * date scope. The active period is stored in URL params (`period`, `from`,
 * `to`) on whichever route is mounted, and mirrored to localStorage so
 * re-entering a different route preserves the selection.
 *
 * Consumers: `AppHeader` (renders the picker) and per-page logic that needs
 * `dateRange` (`FaturasScreen`, `TransactionsScreen`, …).
 */
export function usePeriodSelection(): {
  value: PeriodSelection;
  onChange: (next: PeriodSelection) => void;
} {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const value = useMemo<PeriodSelection>(() => {
    const resolved = resolvePeriodSelectionParams(searchParams);
    const range = computePeriodDateRange(resolved.preset, resolved.from, resolved.to);
    return { preset: resolved.preset, ...range };
  }, [searchParams]);

  const onChange = useCallback(
    (next: PeriodSelection) => {
      writeStoredPeriodSelection(next);
      const params = new URLSearchParams(searchParams.toString());
      params.set('period', next.preset);
      if (next.preset === 'custom') {
        params.set('from', format(next.from, 'yyyy-MM-dd'));
        params.set('to', format(next.to, 'yyyy-MM-dd'));
      } else {
        params.delete('from');
        params.delete('to');
      }
      // Reset paginated state on period change.
      params.delete('page');
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [pathname, router, searchParams],
  );

  return { value, onChange };
}

/** Convenience for consumers that only need the preset id. */
export function usePeriodPreset(): PeriodPreset {
  return usePeriodSelection().value.preset;
}
